import type {
  BoardCard,
  BoardChange,
  BoardColumn,
  BoardColumnView,
  BoardMoveResult,
  BoardTime,
  BoardView,
  CaptureParse,
  EstimateDefaults,
  Item,
} from '@hugo/core'
import { formatDay } from './format'
import { defaultMinutes } from './plan'
import { todayDuration } from './today'

/**
 * Words and moves for the board. Every number comes from the API (`board.view`, hugo's planner);
 * these functions only put them into words, find neighbours for the keyboard and move a card in
 * the cache until the server answers.
 */

export const COLUMN_LABELS: Record<BoardColumn, string> = {
  backlog: 'Backlog',
  next_week: 'Next week',
  this_week: 'This week',
  tomorrow: 'Tomorrow',
  focus: 'Focus work',
  today: 'Today',
  now: 'Now',
  waiting: 'Waiting for others',
  done: 'Done',
}

/** The three columns of today, the board's focus. */
export const TODAY_COLUMNS: readonly BoardColumn[] = ['focus', 'today', 'now']

/** Columns whose cards have no order of the user's (by follow-up date, by when done). */
const UNORDERED = new Set<BoardColumn>(['waiting', 'done'])

/**
 * The list a card added in a column goes to: Waiting holds waiting-on items; a work column plans
 * work, so a text that reads like a wait is a task there (as item.capture decides).
 */
export function composerKind(
  column: BoardColumn,
  parsed: CaptureParse['kind'],
): CaptureParse['kind'] {
  if (column === 'waiting') return 'waiting'
  return parsed === 'waiting' ? 'task' : parsed
}

/** The time a task or promise needs: the user's estimate, else hugo's default for its type. */
export function timeNeeded(
  item: Pick<Item, 'estimateMinutes' | 'type' | 'plan'>,
  defaults?: EstimateDefaults,
): number {
  return item.estimateMinutes ?? defaultMinutes(item, defaults)
}

/** The toast after a move: "Moved to Today", "Waiting for others", "Marked done". */
export function movedTitle(column: BoardColumn): string {
  switch (column) {
    case 'done':
      return 'Marked done'
    case 'waiting':
      return 'Waiting for others'
    case 'focus':
      return 'Focus work today'
    default:
      return `Moved to ${COLUMN_LABELS[column]}`
  }
}

/** The days a planned card stands for, in words: "today", "tomorrow", "this week", "Fri 23 Oct". */
function periodOf(
  column: BoardColumn | null,
  card?: Pick<BoardCard['item'], 'planLane' | 'planDate'>,
  thisYear: number | null = null,
): string {
  if (column === 'focus' || column === 'today' || column === 'now') return 'today'
  if (column === 'tomorrow') return 'tomorrow'
  const day = card?.planLane === 'day' && card.planDate ? formatDay(card.planDate, thisYear) : null
  if (column === 'this_week') return day ?? 'this week'
  if (column === 'next_week') return day ?? 'next week'
  if (day) return day
  if (card?.planLane === 'week' && card.planDate) {
    return `the week of ${formatDay(card.planDate, thisYear)}`
  }
  return 'its day'
}

const likely = (projectedOn: string | null, thisYear: number) =>
  projectedOn ? `likely ${formatDay(projectedOn, thisYear)}` : 'not within a year'

/** One consequence of a move, as the toast names it. */
export function changeText(change: BoardChange, thisYear: number): string {
  switch (change.kind) {
    case 'late':
      return `${change.title} is late now (${likely(change.projectedOn, thisYear)})`
    case 'no_fit':
      return `${change.title} no longer fits ${periodOf(change.to)} (${likely(change.projectedOn, thisYear)})`
    case 'on_time':
      return `${change.title} is on time again`
    case 'fits':
      return `${change.title} fits ${periodOf(change.to)} again`
    case 'moved':
      return `hugo moved ${change.title} to ${COLUMN_LABELS[change.to ?? 'backlog']}`
  }
}

/**
 * What the toast says under its title: whether the moved card fits, then up to two consequences
 * for other cards, the most important first ("and 2 more" for the rest). Null when nothing else
 * changed.
 */
export function moveDetail(result: BoardMoveResult, thisYear: number): string | null {
  const lines: string[] = []
  if (result.fits === false) {
    lines.push(
      `Doesn't fit ${periodOf(result.column, result.item, thisYear)} (${likely(result.item.plan?.projectedOn ?? null, thisYear)})`,
    )
  }
  const shown = result.changes.slice(0, 2)
  for (const change of shown) lines.push(changeText(change, thisYear))
  const more = result.changes.length - shown.length
  if (more > 0) lines.push(`and ${more} more`)
  return lines.length > 0 ? lines.join('. ') : null
}

/** A column's time: "2 h of 2 h planned", or "No time for tasks". */
export function timeLabel(time: BoardTime): string {
  if (time.capacityMinutes === 0) return 'No time for tasks'
  return `${todayDuration(time.plannedMinutes)} of ${todayDuration(time.capacityMinutes)} planned`
}

/** Meetings and out-of-office time in a column's days: "Meetings 1.5 h"; null without. */
export function busyLabel(time: BoardTime): string | null {
  const parts: string[] = []
  if (time.meetingMinutes > 0) parts.push(`Meetings ${todayDuration(time.meetingMinutes)}`)
  if (time.outOfOfficeMinutes > 0) {
    parts.push(`Out of office ${todayDuration(time.outOfOfficeMinutes)}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/** The days a column stands for: "Thu 22 Oct", "Fri 23 – Sun 25 Oct"; null for others. */
export function spanLabel(column: BoardColumnView, thisYear: number): string | null {
  if (!column.from || !column.to) return null
  if (column.from === column.to) return formatDay(column.from, thisYear)
  return `${formatDay(column.from, thisYear)} – ${formatDay(column.to, thisYear)}`
}

export type CardLine = { text: string; tone: 'muted' | 'attention' }

/**
 * The plan line on a card. Planned by the user: whether it fits ("Doesn't fit today · likely Thu
 * 22 Oct"), else the time it gets today or when it is done. hugo's own: when hugo does it.
 * Waiting: the follow-up day; done: the day; snoozed: until when (the card is dimmed too).
 */
export function cardLine(
  card: BoardCard,
  column: BoardColumn,
  context: { today: string; thisYear: number; dayOf: (iso: string) => string },
): CardLine | null {
  const { item } = card
  const day = (date: string) => formatDay(date, context.thisYear)
  if (column === 'done') {
    return item.completedAt
      ? { text: `Done ${day(context.dayOf(item.completedAt))}`, tone: 'muted' }
      : null
  }
  if (column === 'waiting') {
    return item.dueAt
      ? { text: `Follow up ${day(context.dayOf(item.dueAt))}`, tone: 'muted' }
      : { text: 'No follow-up date', tone: 'muted' }
  }
  const plan = item.plan
  if (item.status === 'snoozed' && item.snoozedUntil) {
    return { text: `Snoozed until ${day(context.dayOf(item.snoozedUntil))}`, tone: 'muted' }
  }
  if (card.personal) {
    // Personal: hugo plans none of the working hours for it (decision 97).
    return item.dueAt
      ? { text: `Personal · due ${day(context.dayOf(item.dueAt))}`, tone: 'muted' }
      : { text: 'Personal · outside your working hours', tone: 'muted' }
  }
  if (!plan) return card.committed ? { text: 'Not planned yet', tone: 'muted' } : null
  if (card.fits === false) {
    return {
      text: `Doesn't fit ${periodOf(column, item, context.thisYear)} · ${likely(plan.projectedOn, context.thisYear)}`,
      tone: 'attention',
    }
  }
  if (plan.status !== 'on_track') {
    return { text: `Late · ${likely(plan.projectedOn, context.thisYear)}`, tone: 'attention' }
  }
  const doneToday = plan.projectedOn === context.today
  if (plan.todayMinutes > 0) {
    const rest = doneToday || !plan.projectedOn ? '' : ` · done ${day(plan.projectedOn)}`
    return { text: `${todayDuration(plan.todayMinutes)} today${rest}`, tone: 'muted' }
  }
  if (!card.committed) {
    if (!plan.startOn) return { text: 'Not within a year', tone: 'muted' }
    const when =
      plan.projectedOn && plan.projectedOn !== plan.startOn
        ? `${day(plan.startOn)} – ${day(plan.projectedOn)}`
        : day(plan.startOn)
    return { text: `hugo plans it ${when}`, tone: 'muted' }
  }
  return plan.projectedOn ? { text: `Done by ${day(plan.projectedOn)}`, tone: 'muted' } : null
}

/** Today's focus time against the focus work: "3 h of 7 h focus time"; warns when it is more. */
export function focusLabel(today: BoardView['today']): CardLine {
  const work = today.focusWorkMinutes
  const time = today.focusMinutes
  if (time === 0) {
    return work > 0
      ? { text: 'No focus time left today', tone: 'attention' }
      : { text: 'No focus time left today', tone: 'muted' }
  }
  if (work === 0) return { text: `${todayDuration(time)} of focus time free`, tone: 'muted' }
  if (work > time) {
    return {
      text: `${todayDuration(work)} of focus work, ${todayDuration(time)} of focus time`,
      tone: 'attention',
    }
  }
  return { text: `${todayDuration(work)} of ${todayDuration(time)} focus time`, tone: 'muted' }
}

/** Where a card is: its column and its index there. */
export function findCard(
  columns: readonly BoardColumnView[],
  id: string,
): { column: BoardColumnView; index: number } | null {
  for (const column of columns) {
    const index = column.cards.findIndex((card) => card.item.id === id)
    if (index >= 0) return { column, index }
  }
  return null
}

/**
 * The card to select from the keyboard: up and down in a column; left and right to the nearest
 * card at about the same height in the next column that has cards. With nothing selected, the
 * first card of today (focus work, today, now), else the first card at all.
 */
export function neighbour(
  columns: readonly BoardColumnView[],
  selectedId: string | null,
  direction: 'up' | 'down' | 'left' | 'right',
): string | null {
  const at = selectedId ? findCard(columns, selectedId) : null
  if (!at) {
    const first =
      TODAY_COLUMNS.map((id) => columns.find((c) => c.id === id)?.cards[0]).find(Boolean) ??
      columns.find((c) => c.cards.length > 0)?.cards[0]
    return first?.item.id ?? null
  }
  const { column, index } = at
  if (direction === 'up' || direction === 'down') {
    const next = column.cards[index + (direction === 'up' ? -1 : 1)]
    return (next ?? column.cards[index])?.item.id ?? null
  }
  const step = direction === 'left' ? -1 : 1
  for (let i = columns.indexOf(column) + step; i >= 0 && i < columns.length; i += step) {
    const cards = columns[i]?.cards ?? []
    if (cards.length > 0) return cards[Math.min(index, cards.length - 1)]?.item.id ?? null
  }
  return selectedId
}

/** The column next to a card's, to move it with Shift and the arrows; null at the edge. */
export function columnBeside(
  columns: readonly BoardColumnView[],
  column: BoardColumn,
  direction: 'left' | 'right',
): BoardColumn | null {
  const index = columns.findIndex((c) => c.id === column)
  return columns[index + (direction === 'left' ? -1 : 1)]?.id ?? null
}

/**
 * A card's place among the cards the user put in a column (what `item.move` takes as
 * `position`), from where it was dropped: before or after the card under the pointer, or at
 * the end (dropped on the column, or on one of hugo's own cards, which come after the user's).
 * Undefined for waiting and done, which have no order of the user's.
 */
export function dropPosition(
  column: BoardColumnView,
  draggedId: string,
  target: { cardId: string; after: boolean } | null,
): number | undefined {
  if (UNORDERED.has(column.id)) return undefined
  const committed = column.cards
    .filter((card) => card.committed && card.item.id !== draggedId)
    .map((card) => card.item.id)
  if (!target) return committed.length
  const index = committed.indexOf(target.cardId)
  if (index < 0) return committed.length
  return target.after ? index + 1 : index
}

/** Where a card is among the user's cards in its column (its `position`); null for hugo's own. */
export function positionOf(column: BoardColumnView, id: string): number | null {
  const committed = column.cards.filter((card) => card.committed)
  const index = committed.findIndex((card) => card.item.id === id)
  return index >= 0 ? index : null
}

/**
 * The board right after a move, until the server's answer replaces it: the card leaves its
 * column and joins the other at its place among the user's cards (waiting and done: first). Its
 * plan and fit are unknown until then.
 */
export function moveLocally(
  view: BoardView,
  id: string,
  to: BoardColumn,
  position: number | undefined,
): BoardView {
  const at = findCard(view.columns, id)
  if (!at) return view
  const moved = at.column.cards[at.index] as BoardCard
  const card: BoardCard = {
    ...moved,
    committed: !UNORDERED.has(to),
    fits: null,
    carriedFrom: null,
  }
  return {
    ...view,
    columns: view.columns.map((column) => {
      const cards = column.cards.filter((entry) => entry.item.id !== id)
      if (column.id !== to)
        return cards.length === column.cards.length ? column : { ...column, cards }
      if (UNORDERED.has(to)) return { ...column, cards: [card, ...cards] }
      const committed = cards.filter((entry) => entry.committed)
      const index = Math.min(position ?? committed.length, committed.length)
      return {
        ...column,
        cards: [
          ...committed.slice(0, index),
          card,
          ...committed.slice(index),
          ...cards.filter((entry) => !entry.committed),
        ],
      }
    }),
  }
}
